import { useState, useEffect } from 'react'
import { Box, TextField, GridLegacy, Typography, Paper, Table, TableBody, TableCell, TableHead, TableRow, Card, CardContent, Button, CircularProgress } from '@mui/material'
import { Helmet } from 'react-helmet-async'
import { useNavigate, useParams, useLocation } from 'react-router'
import toast from 'react-hot-toast'
import SubHeader from 'src/components/SubHeader'
import { ordersApi } from 'src/api/ordersApi'
import numeral from 'numeral'

interface ProductItem {
    productId: string
    name: string
    catalogueId?: string
    catalogueName?: string
    categoryId?: string
    categoryName?: string
    sku?: string
    quantity: number
    priceType?: 'monthlyMsrp' | 'oneYearMsrp' | 'threeYearMsrp'
    price: number
    total: number
}

interface OrderData {
    _id?: string
    id?: string
    orderNumber: string
    fromOrganization?: any
    toOrganization?: any
    orderDate: string
    vat: number
    note?: string
    products: ProductItem[]
    subtotal?: number
    subTotal?: number
    vatAmount?: number
    total?: number
    status?: string
    isActive?: boolean
    createdAt?: string
    updatedAt?: string
    createdBy?: {
        _id: string
        name: string
    }
    updatedBy?: {
        _id: string
        name: string
    }
}

function ViewOrder() {
    const navigate = useNavigate()
    const location = useLocation()
    const { orderId } = useParams<{ orderId: string }>()
    const [order, setOrder] = useState<OrderData | null>(null)
    const [loading, setLoading] = useState(true)

    // Determine if we're viewing an invoice or order
    const isInvoice = location.pathname.includes('/invoices/')

    useEffect(() => {
        const fetchOrder = async () => {
            const entityType = isInvoice ? 'Invoice' : 'Order'
            const backRoute = isInvoice ? '/invoices' : '/orders'

            if (!orderId) {
                toast.error(`${entityType} ID is missing`)
                navigate(backRoute)
                return
            }

            try {
                setLoading(true)
                const response = await ordersApi.getOrderById(orderId)

                if (response?.data) {
                    const orderData = response.data
                    setOrder(orderData)
                } else {
                    toast.error(`Failed to load ${entityType.toLowerCase()} details`)
                    navigate(backRoute)
                }
            } catch (error: any) {
                console.error(`Error fetching ${entityType.toLowerCase()}:`, error)
                const errorMessage = error?.response?.data?.message || `Failed to load ${entityType.toLowerCase()} details`
                toast.error(errorMessage)
                navigate(backRoute)
            } finally {
                setLoading(false)
            }
        }

        fetchOrder()
    }, [orderId, navigate, isInvoice])

    const entityType = isInvoice ? 'Invoice' : 'Order'
    const backRoute = isInvoice ? '/orders/invoices' : '/orders'

    if (loading) {
        return (
            <>
                <Helmet>
                    <title>IT22 ERP | View {entityType}</title>
                </Helmet>
                <SubHeader title={`View ${entityType}`} />
                <Box px="30px" py="20px" display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                    <CircularProgress />
                </Box>
            </>
        )
    }

    if (!order) {
        return null
    }

    // Calculate totals
    const subTotal = order.subtotal || order.subTotal || order.products?.reduce((sum, p) => sum + (p.total || 0), 0) || 0
    const vatRate = typeof order.vat === 'string' ? parseFloat(order.vat) : (order.vat || 0)
    const vatAmount = order.vatAmount || (subTotal * vatRate) / 100
    const total = order.total || subTotal + vatAmount

    // Format date
    const formatDate = (dateString: string) => {
        if (!dateString) return '-'
        try {
            const date = new Date(dateString)
            const day = String(date.getDate()).padStart(2, '0')
            const month = String(date.getMonth() + 1).padStart(2, '0')
            const year = date.getFullYear()
            return `${day}/${month}/${year}`
        } catch {
            return dateString
        }
    }

    const getPriceTypeLabel = (priceType?: string) => {
        if (priceType === 'monthlyMsrp') return 'Monthly MSRP'
        if (priceType === 'oneYearMsrp') return '1-Year MSRP'
        if (priceType === 'threeYearMsrp') return '3-Year MSRP'
        return '-'
    }

    // Extract organization names
    const fromOrgName = order.fromOrganization?.name || '-'
    const toOrgName = order.toOrganization?.name || '-'

    return (
        <>
            <Helmet>
                <title>IT22 ERP | View {entityType}</title>
            </Helmet>
            <SubHeader title={`View ${entityType}`} />
            <Box px="30px" py="20px">
                <GridLegacy container spacing={3}>
                    {/* Order Details Row */}
                    <GridLegacy item xs={12}>
                        <GridLegacy container spacing={2}>
                            <GridLegacy item xs={12} md={3}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label={`${entityType} #`}
                                    value={order.orderNumber || '-'}
                                    variant="outlined"
                                    InputProps={{
                                        readOnly: true,
                                    }}
                                />
                            </GridLegacy>

                            <GridLegacy item xs={12} md={3}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="From Organization"
                                    value={fromOrgName}
                                    variant="outlined"
                                    InputProps={{
                                        readOnly: true,
                                    }}
                                />
                            </GridLegacy>

                            <GridLegacy item xs={12} md={3}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="To Organization"
                                    value={toOrgName}
                                    variant="outlined"
                                    InputProps={{
                                        readOnly: true,
                                    }}
                                />
                            </GridLegacy>

                            <GridLegacy item xs={12} md={3}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Date"
                                    value={formatDate(order.orderDate)}
                                    variant="outlined"
                                    InputProps={{
                                        readOnly: true,
                                    }}
                                />
                            </GridLegacy>

                            <GridLegacy item xs={12} md={3}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="VAT %"
                                    value={`${vatRate}%`}
                                    variant="outlined"
                                    InputProps={{
                                        readOnly: true,
                                    }}
                                />
                            </GridLegacy>


                        </GridLegacy>
                    </GridLegacy>

                    {/* Products Section */}
                    <GridLegacy item xs={12}>
                        <Paper elevation={1} style={{ padding: '20px' }}>
                            <Typography variant="h6" gutterBottom>
                                Products ({order.products?.length || 0})
                            </Typography>

                            {!order.products || order.products.length === 0 ? (
                                <Box
                                    display="flex"
                                    justifyContent="center"
                                    alignItems="center"
                                    minHeight="200px"
                                >
                                    <Typography color="textSecondary">
                                        No Products
                                    </Typography>
                                </Box>
                            ) : (
                                <Box sx={{ overflowX: 'auto', mt: 2 }}>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell style={{ padding: '8px 16px' }}>#</TableCell>
                                                <TableCell style={{ padding: '8px 16px' }}>CATALOGUE</TableCell>
                                                <TableCell style={{ padding: '8px 16px' }}>CATEGORY</TableCell>
                                                <TableCell style={{ padding: '8px 16px' }}>SKU</TableCell>
                                                <TableCell style={{ padding: '8px 16px' }}>NAME</TableCell>
                                                <TableCell style={{ padding: '8px 16px' }}>QUANTITY</TableCell>
                                                <TableCell style={{ padding: '8px 16px' }}>PRICE TYPE</TableCell>
                                                <TableCell style={{ padding: '8px 16px' }}>PRICE</TableCell>
                                                <TableCell style={{ padding: '8px 16px' }}>TOTAL</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {order.products.map((product, index) => (
                                                <TableRow key={product.productId}>
                                                    <TableCell style={{ padding: '8px 16px' }}>{index + 1}</TableCell>
                                                    <TableCell style={{ padding: '8px 16px' }}>{product.catalogueName || '-'}</TableCell>
                                                    <TableCell style={{ padding: '8px 16px' }}>{product.categoryName || '-'}</TableCell>
                                                    <TableCell style={{ padding: '8px 16px' }}>{product.sku || '-'}</TableCell>
                                                    <TableCell style={{ padding: '8px 16px', maxWidth: '200px' }}>
                                                        {product.name || '-'}
                                                    </TableCell>
                                                    <TableCell style={{ padding: '8px 16px' }}>{product.quantity || 0}</TableCell>
                                                    <TableCell style={{ padding: '8px 16px' }}>
                                                        {getPriceTypeLabel(product.priceType)}
                                                    </TableCell>
                                                    <TableCell style={{ padding: '8px 16px' }}>
                                                        {numeral(product.price || 0).format('€0,0.00')}
                                                    </TableCell>
                                                    <TableCell style={{ padding: '8px 16px' }}>
                                                        {numeral(product.total || 0).format('€0,0.00')}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </Box>
                            )}

                            <Box style={{ display: 'flex', justifyContent: 'space-between', flexDirection: 'row', marginTop: '20px' }}>
                                <Box position="relative" style={{ width: '50%' }}>
                                    {order.note && (
                                        <TextField
                                            fullWidth
                                            name="notes"
                                            label="Notes and Disclaimer"
                                            value={order.note}
                                            variant="outlined"
                                            size="small"
                                            multiline
                                            rows={4}
                                            InputProps={{
                                                readOnly: true,
                                            }}
                                        />
                                    )}
                                </Box>
                                <Box position="relative" style={{ width: '30%' }}>
                                    <Card>
                                        <CardContent>
                                            <Box display="flex" justifyContent="space-between" mb={1}>
                                                <Typography variant="body2">Sub Total</Typography>
                                                <Typography variant="body2">
                                                    {numeral(subTotal).format('€0,0.00')}
                                                </Typography>
                                            </Box>
                                            <Box display="flex" justifyContent="space-between" mb={1}>
                                                <Typography variant="body2">VAT ({vatRate}%)</Typography>
                                                <Typography variant="body2">
                                                    {numeral(vatAmount).format('€0,0.00')}
                                                </Typography>
                                            </Box>
                                            <Box display="flex" justifyContent="space-between" mb={2}>
                                                <Typography variant="h6">Total</Typography>
                                                <Typography variant="h6">
                                                    {numeral(total).format('€0,0.00')}
                                                </Typography>
                                            </Box>
                                            <Box display="flex" justifyContent="flex-end" gap="10px" mt={3}>
                                                <Button
                                                    variant="outlined"
                                                    onClick={() => navigate(backRoute)}
                                                >
                                                    Back to {entityType}s
                                                </Button>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Box>
                            </Box>
                        </Paper>
                    </GridLegacy>
                </GridLegacy>
            </Box>
        </>
    )
}

export default ViewOrder

